// src/app/core/http/cache.interceptor.ts
import type { HttpInterceptorFn, HttpRequest, HttpHandlerFn} from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, tap } from 'rxjs';
import { CacheStoreService } from './cache-store.service';

/** TTL en milisegundos para endpoints cacheables */
const TTL_2_MIN = 2 * 60 * 1000;

/**
 * Endpoints cacheables y su TTL.
 * Cache key: método + URL sin query params variables.
 *
 * Tabla definida en Fase 0, sección 0.7:
 * - GET /self/clients/{id}                        → 2 min
 * - GET /self/clients/{id}/accounts               → 2 min
 * - GET /self/loans/{id} (sin associations)       → 2 min
 * - GET /self/loans/{id}?associations=...         → NUNCA (datos transaccionales)
 * - GET /self/savingsaccounts/{id}                → NUNCA (saldo cambia)
 */
function getCacheTtl(req: HttpRequest<unknown>): number | null {
  if (req.method !== 'GET') return null;

  const url = req.url;

  // Excluir requests con ?associations= (plan de pagos y transacciones)
  if (req.params.has('associations')) return null;

  // Endpoints cacheables (orden de especificidad descendente)
  if (/\/self\/clients\/\d+\/accounts$/.test(url)) return TTL_2_MIN;
  if (/\/self\/clients\/\d+$/.test(url))           return TTL_2_MIN;
  if (/\/self\/loans\/\d+$/.test(url))             return TTL_2_MIN;

  return null;
}

/**
 * CacheInterceptor — caché en memoria de corta duración para GETs seleccionados.
 *
 * Cache key: "GET:{url}{serializedParams}"
 * La caché vive en CacheStoreService (singleton en memoria).
 * Se destruye completamente al hacer logout.
 */
export const cacheInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const cacheStore = inject(CacheStoreService);
  const ttl = getCacheTtl(req);

  // Si no es cacheable, pasar directamente
  if (ttl === null) {
    return next(req);
  }

  // Construir cache key incluyendo los query params para diferenciar variantes
  const cacheKey = `GET:${req.urlWithParams}`;

  // Verificar si hay una respuesta cacheada válida
  const cached = cacheStore.get(cacheKey);
  if (cached !== null) {
    // Retornar la respuesta cacheada directamente
    return of(new HttpResponse({ body: cached, status: 200 }));
  }

  // No hay caché — hacer el request y almacenar la respuesta
  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.status === 200) {
        cacheStore.set(cacheKey, event.body, ttl);
      }
    })
  );
};
